/**
 * JS port of KollaborateServer's generate_marker() function.
 *
 * Generates marker SVG data at arbitrary pixel dimensions from normalised
 * path coordinates, and provides data-URI helpers for use as CSS
 * background-image values.
 *
 * All path data is stored as normalised [x, y] arrays (0-1 range) derived
 * from the server's 82x91 viewBox.  scalePath() multiplies them into the
 * target coordinate space at generation time.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Point = [number, number];

// ---------------------------------------------------------------------------
// Colour maps  (matching Rust static_handling::generate_marker)
// ---------------------------------------------------------------------------

const MARKER_COLORS: Record<string, [string, string]> = {
  red: ['#5a0308', '#c64a4d'],
  yellow: ['#dfe52e', '#dee34f'],
  orange: ['#d68f1c', '#b37410'],
  green: ['#1cd62e', '#0c820e'],
  blue: ['#1c54d6', '#0c2d82'],
  cyan: ['#1cc7d6', '#10b3a8'],
  pink: ['#d66ba4', '#cc6685'],
  purple: ['#5a1cd6', '#6410b3'],
  white: ['#eeeeee', '#cccccc'],
  black: ['#333333', '#0d0d0d'],
  blank: ['#a6a6a6', '#b3b3b3'],
};

const COLOR_INDEX_MAP: Record<number, string> = {
  0: 'blank',
  1: 'red',
  2: 'yellow',
  3: 'orange',
  4: 'green',
  5: 'blue',
  6: 'cyan',
  7: 'pink',
  8: 'purple',
  9: 'white',
  10: 'black',
};

// ---------------------------------------------------------------------------
// Normalised path data  (original 82x91 viewBox, divided by 82 / 91)
// ---------------------------------------------------------------------------

// Border outline (the larger outer shape, fill="#000" in the server SVG)
// Original: M78.962,54.057 L39.457,89.218 L3.015,54.058 H3.008
//           L3,2.384 L78.92,2.376 l0.024,51.681
const BORDER_POINTS: Point[] = [
  [78.962 / 82, 54.057 / 91],
  [39.457 / 82, 89.218 / 91],
  [3.015 / 82, 54.058 / 91],
  [3.008 / 82, 54.058 / 91],
  [3 / 82, 2.384 / 91],
  [78.92 / 82, 2.376 / 91],
  [78.944 / 82, 54.057 / 91], // l0.024,51.681 resolved to absolute
];

// Inner fill shape (the coloured area, slightly inset from border)
// Original: M75.83,52.4 L39.441,84.876 L5.874,52.4 H5.868
//           L5.856,4.665 l69.935,-0.008 L75.813,52.4
const FILL_POINTS: Point[] = [
  [75.83 / 82, 52.4 / 91],
  [39.441 / 82, 84.876 / 91],
  [5.874 / 82, 52.4 / 91],
  [5.868 / 82, 52.4 / 91],
  [5.856 / 82, 4.665 / 91],
  [75.791 / 82, 4.657 / 91], // l69.935,-0.008 resolved to absolute
  [75.813 / 82, 52.4 / 91],
];

// Highlight (white semi-transparent streak for 3D depth)
// Original: M26.009,65.469 L39.476,79.26 L69.64,50.629
//           l1.237,-38.219 l-8.24,0.121 Z
const HIGHLIGHT_POINTS: Point[] = [
  [26.009 / 82, 65.469 / 91],
  [39.476 / 82, 79.26 / 91],
  [69.64 / 82, 50.629 / 91],
  [70.877 / 82, 12.41 / 91], // l1.237,-38.219 resolved
  [62.637 / 82, 12.531 / 91], // l-8.24,0.121 resolved
];

// ---------------------------------------------------------------------------
// Path helpers
// ---------------------------------------------------------------------------

/** Native aspect ratio of the marker artwork (height / width). */
export const ASPECT = 91 / 82;

/** Default corner radius in normalised coordinates (0-1). */
const CORNER_RADIUS = 0.12;

/** Indices into BORDER_POINTS / FILL_POINTS to round (top-left, top-right). */
const ROUND_INDICES = new Set([4, 5]);

/**
 * Scale normalised points into an SVG path string at the given dimensions.
 *
 * When w and h are both <= 1 (objectBoundingBox coordinates), 4 decimal
 * places are used for precision.  Otherwise 2 decimals (pixel coords).
 *
 * When `cornerRadius` > 0 and `roundIndices` is provided, vertices at those
 * indices get a quadratic Bézier curve (Q command) instead of a sharp corner.
 * The radius controls how far back along each adjacent edge the curve starts.
 */
function scalePath(
  points: Point[],
  w: number,
  h: number,
  close = false,
  cornerRadius = 0,
  roundIndices?: Set<number>,
): string {
  const decimals = w <= 1 && h <= 1 ? 4 : 2;
  const fmt = (v: number) => v.toFixed(decimals);

  // Fast path — no rounding
  if (cornerRadius <= 0 || !roundIndices || roundIndices.size === 0) {
    const [first, ...rest] = points;
    let d = `M${fmt(first[0] * w)},${fmt(first[1] * h)}`;
    for (const [x, y] of rest) {
      d += `L${fmt(x * w)},${fmt(y * h)}`;
    }
    if (close) d += 'Z';
    return d;
  }

  // Rounded path — Q curves at selected corners
  const n = points.length;
  let d = '';
  for (let i = 0; i < n; i++) {
    const [px, py] = points[i];

    if (!roundIndices.has(i)) {
      d += i === 0
        ? `M${fmt(px * w)},${fmt(py * h)}`
        : `L${fmt(px * w)},${fmt(py * h)}`;
      continue;
    }

    // Compute pull-back along incoming edge and pull-forward along outgoing edge
    const prev = points[(i - 1 + n) % n];
    const next = points[(i + 1) % n];

    const dx_in = prev[0] - px;
    const dy_in = prev[1] - py;
    const len_in = Math.sqrt(dx_in * dx_in + dy_in * dy_in);
    const dx_out = next[0] - px;
    const dy_out = next[1] - py;
    const len_out = Math.sqrt(dx_out * dx_out + dy_out * dy_out);

    // Don't exceed 40% of either adjacent edge
    const r_in = Math.min(cornerRadius, len_in * 0.4);
    const r_out = Math.min(cornerRadius, len_out * 0.4);

    const ax = px + (dx_in / len_in) * r_in;
    const ay = py + (dy_in / len_in) * r_in;
    const bx = px + (dx_out / len_out) * r_out;
    const by = py + (dy_out / len_out) * r_out;

    // L to pull-back point, Q through vertex to pull-forward point
    const cmd = i === 0 ? 'M' : 'L';
    d += `${cmd}${fmt(ax * w)},${fmt(ay * h)}`;
    d += `Q${fmt(px * w)},${fmt(py * h)} ${fmt(bx * w)},${fmt(by * h)}`;
  }
  if (close) d += 'Z';
  return d;
}

// ---------------------------------------------------------------------------
// Exports — clip path
// ---------------------------------------------------------------------------

/**
 * Border outline as an SVG path string in objectBoundingBox coordinates
 * (0-1 range).  Use with <clipPath clipPathUnits="objectBoundingBox">.
 */
export const MARKER_CLIP_PATH = scalePath(BORDER_POINTS, 1, 1, true);

let clipPathRounded: string | null = null;

/**
 * Return the marker border clip-path string.  When `rounded` is true the
 * top-left and top-right corners use quadratic Bézier curves.
 */
export function getMarkerClipPath(rounded: boolean): string {
  if (!rounded) return MARKER_CLIP_PATH;
  if (!clipPathRounded) {
    clipPathRounded = scalePath(BORDER_POINTS, 1, 1, true, CORNER_RADIUS, ROUND_INDICES);
  }
  return clipPathRounded;
}

// ---------------------------------------------------------------------------
// Exports — colour lookup
// ---------------------------------------------------------------------------

export function getMarkerColors(
  colorIndex: number,
): { name: string; color1: string; color2: string } {
  const name = COLOR_INDEX_MAP[colorIndex] ?? 'blank';
  const [color1, color2] = MARKER_COLORS[name] ?? MARKER_COLORS.blank;
  return { name, color1, color2 };
}

// ---------------------------------------------------------------------------
// Exports — SVG generation
// ---------------------------------------------------------------------------

/**
 * Generate the fill-only marker SVG string (no border outline) at exact
 * pixel dimensions.
 *
 * @param color1   Gradient start colour (top)
 * @param color2   Gradient end colour (bottom)
 * @param w        Width in pixels
 * @param h        Height in pixels
 * @param rounded  Round the top corners with quadratic Bézier curves
 */
export function buildMarkerSvg(
  color1: string,
  color2: string,
  w: number,
  h: number,
  rounded = false,
): string {
  const r = rounded ? CORNER_RADIUS : 0;
  const ri = rounded ? ROUND_INDICES : undefined;
  const fillD = scalePath(FILL_POINTS, w, h, false, r, ri);
  const highD = scalePath(HIGHLIGHT_POINTS, w, h, true);

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">` +
    '<defs>' +
    '<linearGradient id="fg" x1="0" y1="0" x2="0" y2="1">' +
    `<stop offset="0" stop-color="${color1}"/>` +
    `<stop offset="1" stop-color="${color2}"/>` +
    '</linearGradient>' +
    '<linearGradient id="sh" x1="0.3" y1="1" x2="0.7" y2="0">' +
    '<stop offset="0" stop-color="#000" stop-opacity="0.25"/>' +
    '<stop offset="0.45" stop-color="#000" stop-opacity="0.08"/>' +
    '<stop offset="1" stop-color="#000" stop-opacity="0"/>' +
    '</linearGradient>' +
    `<clipPath id="cl"><path d="${fillD}"/></clipPath>` +
    '</defs>' +
    `<path d="${fillD}" fill="url(#fg)" fill-rule="evenodd"/>` +
    `<path d="${fillD}" fill="url(#sh)" fill-rule="evenodd" clip-path="url(#cl)"/>` +
    `<path d="${highD}" fill="#fff" fill-opacity="0.25" fill-rule="evenodd"/>` +
    '</svg>'
  );
}

// ---------------------------------------------------------------------------
// Exports — data-URI helper (with lazy cache)
// ---------------------------------------------------------------------------

function svgToDataUri(svg: string): string {
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

const fillUriCache = new Map<string, string>();

/**
 * Return a CSS `url("data:…")` value for a marker fill SVG.
 *
 * @param colorIndex  Colour index (0-10, maps to named colours)
 * @param width       Target width in pixels
 * @param height      Target height in pixels (omit to preserve native ratio)
 * @param rounded     Round the top corners with quadratic Bézier curves
 */
export function getMarkerFillUri(
  colorIndex: number,
  width: number,
  height?: number,
  rounded = false,
): string {
  const h = height ?? Math.round(width * ASPECT);
  const key = `${colorIndex}-${width}-${h}${rounded ? '-r' : ''}`;

  let uri = fillUriCache.get(key);
  if (uri) return uri;

  const name = COLOR_INDEX_MAP[colorIndex] ?? 'blank';
  const [c1, c2] = MARKER_COLORS[name] ?? MARKER_COLORS.blank;
  uri = svgToDataUri(buildMarkerSvg(c1, c2, width, h, rounded));
  fillUriCache.set(key, uri);
  return uri;
}
